import { createContext, useContext, type ReactNode } from 'react'

import { TEXTES, type Cles, type Langue } from './i18n'

/**
 * La langue courante, disponible partout sans passage de props.
 *
 * **Pourquoi un contexte plutôt qu'une prop de plus.** Quatre écrans antérieurs à la règle
 * « aucune chaîne dans le JSX » ne reçoivent pas `langue` : leur en ajouter une la ferait
 * traverser chaque composant intermédiaire, et le prochain écran écrit sans elle retomberait
 * silencieusement dans le français en dur — c'est exactement le mécanisme qui a produit la dette
 * que L9 solde.
 *
 * **Ce que cela prépare pour L10.** Le §6 de la conception de la refonte demande qu'aucune chaîne
 * ne vive dans le JSX. Avec un contexte, un composant neuf obtient ses textes en une ligne, et
 * l'oubli devient plus coûteux que le geste correct — la seule forme de règle qui tienne.
 */
const LangueContexte = createContext<Langue>('fr')

export function FournisseurDeLangue({
  langue,
  children,
}: {
  langue: Langue
  children: ReactNode
}) {
  return <LangueContexte.Provider value={langue}>{children}</LangueContexte.Provider>
}

/** La langue courante — pour `Intl`, pour `dir`, pour un attribut `lang`. */
export function useLangue(): Langue {
  return useContext(LangueContexte)
}

/** Les textes de la langue courante. `const t = useTextes()`, puis `t.maCle`. */
export function useTextes(): Record<Cles, string> {
  return TEXTES[useContext(LangueContexte)]
}
